import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
    Box,
    Typography,
    TextField,
    Grid,
    Button,
    CircularProgress,
    Alert,
    Chip,
    Divider,
    FormGroup,
    FormControlLabel,
    Switch,
    Paper,
    InputAdornment,
    Autocomplete,
    Popper,
    Paper as MuiPaper,
    List,
    ListItemButton,
    ListItemText,
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Phone as PhoneIcon,
    WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import Api2Talk from "../../services/api2talk";
import AddAutomationPlaceholder, {
    PlaceType,
    placeholdersData,
} from "../emailConfig/AddAutomationPlaceholder";

// Provider gating no combo de envio ativo via automacao:
// - WHITELIST do que APARECE: WhatsApp (clicavel) e Telegram (disabled, "em breve").
// - Instagram, Messenger, Webchat, "Aplicativo Conectado" nao aparecem
//   (nao sao provedores de envio ativo via template — ou ainda nao integrados).
const PROVIDER_ID_META_WHATSAPP = 2;
const PROVIDER_ID_TELEGRAM      = 5;
const PROVIDER_IDS_VISIBLE      = [PROVIDER_ID_META_WHATSAPP, PROVIDER_ID_TELEGRAM];
const PROVIDER_IDS_CLICKABLE    = [PROVIDER_ID_META_WHATSAPP]; // Telegram entra dps

const styles = {
    container: {
        padding: "16px",
        backgroundColor: "#fafafa",
        borderRadius: "8px",
        border: "1px solid #e0e0e0",
    },
    sectionTitle: {
        fontSize: "14px",
        fontWeight: 600,
        marginBottom: "8px",
        color: "#333",
    },
    sectionDivider: {
        margin: "16px 0",
    },
    variableRow: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "8px",
    },
    variableLabel: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#555",
        minWidth: "60px",
    },
    phoneChip: {
        marginRight: "8px",
        marginBottom: "8px",
    },
};

// Normalizadores (API retorna lowercase via LowercaseContractResolver).
const normalizeProvider = (p) => ({
    id: p?.id ?? p?.Id,
    name: p?.name ?? p?.Name ?? "",
});

const normalizeNumber = (n) => ({
    id: n?.id ?? n?.Id,
    phoneNumber: n?.phonenumber ?? n?.phoneNumber ?? "",
    namespace: n?.namespace ?? n?.Namespace ?? "",
    messageProviderId: n?.messageproviderid ?? n?.messageProviderId,
});

const normalizeTemplate = (t) => ({
    id: t?.id ?? t?.Id,
    name: t?.name ?? t?.Name ?? "",
    headerText: t?.headertext ?? t?.headerText ?? "",
    bodyText: t?.bodytext ?? t?.bodyText ?? "",
    footerText: t?.footertext ?? t?.footerText ?? "",
    templateType: t?.templatetype ?? t?.templateType,
});

// Extrai variaveis {{1}}, {{2}}, etc. de um texto de template.
const extractVariables = (text) => {
    if (!text) return [];
    const matches = text.match(/\{\{\s*\d+\s*\}\}/g) || [];
    return [...new Set(matches)];
};

// ============================================================
// TemplatePreview — renderiza header/body/footer com {{N}} highlighted como chips inline.
// Quando o usuario preenche o valor da variavel (mapping[section][{{N}}]), o chip exibe esse
// valor em verde; caso contrario fica amarelo com o token {{N}} cru pra orientar onde encaixa.
// ============================================================
const renderTemplateLineWithChips = (text, mappingObj) => {
    if (!text) return null;
    // Split preservando os placeholders {{N}} como tokens separados.
    const parts = String(text).split(/(\{\{\s*\d+\s*\}\})/g);
    return (
        <>
            {parts.map((part, idx) => {
                if (!part) return null;
                if (/^\{\{\s*\d+\s*\}\}$/.test(part)) {
                    const filledValue = (mappingObj || {})[part];
                    const hasValue = filledValue != null && filledValue !== "";
                    return (
                        <Chip
                            key={idx}
                            size="small"
                            label={hasValue ? filledValue : part}
                            sx={{
                                mx: 0.25,
                                height: 22,
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                bgcolor: hasValue ? "#dcfce7" : "#fef3c7",
                                color: hasValue ? "#166534" : "#92400e",
                                border: hasValue ? "1px solid #86efac" : "1px solid #fcd34d",
                                "& .MuiChip-label": { px: 0.75 },
                            }}
                        />
                    );
                }
                return <span key={idx}>{part}</span>;
            })}
        </>
    );
};

const TemplatePreview = ({ templateDetail, mapping }) => {
    if (!templateDetail) return null;
    const { headerText, bodyText, footerText } = templateDetail;
    const hasAnyContent = headerText || bodyText || footerText;
    if (!hasAnyContent) return null;
    return (
        <Paper
            variant="outlined"
            sx={{
                padding: "14px 16px",
                marginBottom: "16px",
                bgcolor: "#f8fafc",
                borderLeft: "4px solid #25D366",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                <WhatsAppIcon sx={{ color: "#25D366", fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", letterSpacing: 0.5 }}>
                    PRÉ-VISUALIZAÇÃO DO TEMPLATE
                </Typography>
            </Box>

            {headerText && (
                <Typography
                    sx={{ fontSize: "0.85rem", fontWeight: 700, mb: 1, color: "#0f172a", whiteSpace: "pre-wrap" }}
                >
                    {renderTemplateLineWithChips(headerText, (mapping || {}).header)}
                </Typography>
            )}

            {bodyText && (
                <Typography
                    sx={{ fontSize: "0.875rem", color: "#1e293b", whiteSpace: "pre-wrap", lineHeight: 1.55 }}
                >
                    {renderTemplateLineWithChips(bodyText, (mapping || {}).body)}
                </Typography>
            )}

            {footerText && (
                <Typography
                    sx={{ fontSize: "0.75rem", color: "#64748b", mt: 1, fontStyle: "italic", whiteSpace: "pre-wrap" }}
                >
                    {footerText}
                </Typography>
            )}
        </Paper>
    );
};

const validateE164 = (raw) => {
    if (!raw) return false;
    const cleaned = raw.replace(/[^\d+]/g, "");
    return /^\+?[1-9]\d{7,14}$/.test(cleaned);
};

// ============================================================
// VariableValueField — TextField composto reusado em header/body/button
// Dois caminhos pra inserir placeholder:
//   (1) Botao AddAutomationPlaceholder ao lado direito (modal padrao do projeto)
//   (2) Autocomplete inline: ao digitar "{{" mostra dropdown filtrando placeholdersData
// ============================================================
// Performance: cada keystroke em VariableValueField propagava onChange → parent state da
// automation-edit (gigante) → re-render de toda a arvore + AddAutomationPlaceholder (669 LOC) a
// cada caractere. Causava digitacao truncada/lenta.
//
// Solucao: estado LOCAL com debounce de 300ms. O input atualiza imediatamente (sem lag),
// e so propaga onChange ao parent apos a pausa. Tambem propagamos no onBlur pra garantir
// que o ultimo valor chegue se o user sair antes de 300ms passar.
// Combinado com React.memo, evita o tsunami de re-renders.
const VariableValueFieldImpl = ({ label, value, onChange, placeholderTypes, literalMode = false }) => {
    const inputRef = useRef(null);
    const [localValue, setLocalValue] = useState(value || "");
    const [anchorEl, setAnchorEl] = useState(null);
    const [suggestionFilter, setSuggestionFilter] = useState("");
    const [openTokenStart, setOpenTokenStart] = useState(-1);

    // Sincroniza quando o value externo muda (ex: load inicial, picker do modal).
    // O guard previne flicker quando o parent ecoa o mesmo valor que ja propagamos.
    useEffect(() => {
        if (value !== localValue) setLocalValue(value || "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // Debounce: propaga pro parent apenas 300ms apos o ultimo keystroke.
    const debounceRef = useRef(null);
    const onChangeRef = useRef(onChange);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

    const schedulePropagate = useCallback((next) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            onChangeRef.current?.(next);
            debounceRef.current = null;
        }, 300);
    }, []);

    // Cleanup pendente no unmount.
    useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

    // Flush imediato (sem esperar debounce) — usado no blur e ao escolher placeholder.
    const flushNow = useCallback((next) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        onChangeRef.current?.(next);
    }, []);

    const suggestions = useMemo(() => {
        const types = placeholderTypes && placeholderTypes.length ? placeholderTypes : [PlaceType.All];
        const inScope = types.includes(PlaceType.All)
            ? placeholdersData
            : placeholdersData.filter((p) => types.includes(p.type));
        if (!suggestionFilter) return inScope.slice(0, 30);
        const lower = suggestionFilter.toLowerCase();
        return inScope
            .filter((p) => p.id.toLowerCase().includes(lower) || (p.name || "").toLowerCase().includes(lower))
            .slice(0, 30);
    }, [suggestionFilter, placeholderTypes]);

    const closePopper = useCallback(() => {
        setAnchorEl(null);
        setOpenTokenStart(-1);
        setSuggestionFilter("");
    }, []);

    const evaluateAutocomplete = useCallback((text, caret, el) => {
        if (caret == null || caret < 2) { closePopper(); return; }
        const before = text.slice(0, caret);
        const idx = before.lastIndexOf("{{");
        if (idx < 0) { closePopper(); return; }
        if (before.slice(idx).includes("}}")) { closePopper(); return; }
        const tokenContent = before.slice(idx + 2);
        setOpenTokenStart(idx);
        setSuggestionFilter(tokenContent);
        setAnchorEl((prev) => prev || el);
    }, [closePopper]);

    const handleChange = (e) => {
        const next = e.target.value;
        setLocalValue(next);            // resposta IMEDIATA no input
        schedulePropagate(next);        // propaga ao parent apos 300ms
        if (!literalMode) evaluateAutocomplete(next, e.target.selectionStart, e.currentTarget);
    };

    const handleBlur = () => {
        // Garante que o ultimo valor chegue ao parent mesmo se sair antes dos 300ms.
        flushNow(localValue);
        setTimeout(closePopper, 120);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Escape") closePopper();
    };

    const handleSelectSuggestion = (placeholder) => {
        const placeholderText = `{{${placeholder.id}}}`;
        const text = localValue || "";
        const caret = inputRef.current?.selectionStart ?? text.length;
        const replaced = text.slice(0, openTokenStart) + placeholderText + text.slice(caret);
        setLocalValue(replaced);
        flushNow(replaced);             // placeholder selecionado → propaga ja
        closePopper();
        setTimeout(() => {
            if (inputRef.current) {
                const pos = openTokenStart + placeholderText.length;
                inputRef.current.focus();
                inputRef.current.setSelectionRange(pos, pos);
            }
        }, 50);
    };

    // Pro AddAutomationPlaceholder (modal padrao): quando ele insere um placeholder,
    // recebe o texto JA modificado. Sincronizamos local + flush.
    const handleModalPlaceholderChange = useCallback((nextText) => {
        setLocalValue(nextText);
        flushNow(nextText);
    }, [flushNow]);

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", width: "100%" }}>
            <Typography sx={styles.variableLabel}>{label}</Typography>
            <TextField
                fullWidth
                size="small"
                inputRef={inputRef}
                placeholder={literalMode ? "Digite o valor" : 'ex: "Olá {{requester.name}}" ou {{ para escolher'}
                value={localValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
            />

            {!literalMode && (<>
            {/* (1) Botao modal padrao do projeto — mantem consistencia com webhook/email. */}
            <AddAutomationPlaceholder
                value={localValue}
                onChange={handleModalPlaceholderChange}
                textAreaRef={inputRef}
                types={placeholderTypes || [PlaceType.All]}
                showChangedFields={false}
            />

            {/* (2) Popper inline com sugestoes quando o usuario digita "{{" */}
            <Popper
                open={Boolean(anchorEl) && suggestions.length > 0}
                anchorEl={anchorEl}
                placement="bottom-start"
                style={{ zIndex: 2000 }}
            >
                <MuiPaper elevation={6} sx={{ maxHeight: 240, width: 320, overflowY: "auto" }}>
                    <List dense disablePadding>
                        {suggestions.map((p) => (
                            <ListItemButton
                                key={p.id}
                                onMouseDown={(e) => { e.preventDefault(); handleSelectSuggestion(p); }}
                            >
                                <ListItemText
                                    primary={<code style={{ fontSize: 13 }}>{`{{${p.id}}}`}</code>}
                                    secondary={<span style={{ fontSize: 12, color: "#666" }}>{p.name}</span>}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </MuiPaper>
            </Popper>
            </>)}
        </Box>
    );
};

// React.memo: corta re-renders quando label/value/placeholderTypes nao mudaram. Junto com
// o estado local + debounce, evita re-render em cascata a cada keystroke dos OUTROS campos
// de variavel (header/body/multiplas).
const VariableValueField = React.memo(VariableValueFieldImpl, (prev, next) => (
    prev.label === next.label
    && prev.value === next.value
    && prev.placeholderTypes === next.placeholderTypes
    && prev.literalMode === next.literalMode
    && prev.onChange === next.onChange
));

/**
 * Componente reutilizavel para configurar o disparo de mensagem ativa via provedor
 * (acao 24 de automacao, mas tambem usavel em outras telas).
 *
 * Fluxo: Provedor -> Numero (filtrado pelo provider) -> Template -> Variaveis -> Destinatarios.
 *
 * Props:
 *   value: { messageproviderid, registrationnumberrequestid, templatemessageid,
 *            parametersmappingjson: { header, body, button },
 *            recipientsjson: { includeRequester, includeAssigned, includeObservant, phones[] } }
 *   onChange: (updated) => void
 *   hirerId: number — usado pra carregar numeros ativos do tenant (gate pelo BD via JWT, esse e so hint)
 *   error: bool — destaca campo principal em vermelho se ha erro de validacao
 */
export default function AutomationActionSendProviderMessage({
    value,
    onChange,
    hirerId,
    error = false,
    directSend = false,
    fixedProviderId = null,
    fixedRegistrationNumberRequestId = null,
    literalVariables = false,
}) {
    const [providers, setProviders] = useState([]);
    const [registrationNumbers, setRegistrationNumbers] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [templateDetail, setTemplateDetail] = useState(null);
    const [loadingProviders, setLoadingProviders] = useState(false);
    const [loadingNumbers, setLoadingNumbers] = useState(false);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [loadingTemplateDetail, setLoadingTemplateDetail] = useState(false);
    const [newPhone, setNewPhone] = useState("");
    const [newPhoneError, setNewPhoneError] = useState("");

    const cfg = value || {};
    const recipients = cfg.recipientsjson || {
        includeRequester: false,
        includeAssigned: false,
        includeObservant: false,
        phones: [],
    };
    const mapping = cfg.parametersmappingjson || { header: {}, body: {}, button: {} };

    const update = (patch) => onChange?.({ ...cfg, ...patch });
    const updateRecipients = (patch) => update({ recipientsjson: { ...recipients, ...patch } });
    const updateMapping = (section, key, val) => {
        const next = { ...mapping };
        next[section] = { ...(next[section] || {}), [key]: val };
        update({ parametersmappingjson: next });
    };
    const updateButtonMapping = (buttonIndex, key, val) => {
        const next = { ...mapping };
        next.button = { ...(next.button || {}) };
        next.button[buttonIndex] = { ...(next.button[buttonIndex] || {}), [key]: val };
        update({ parametersmappingjson: next });
    };

    // ============= FETCHERS =============

    const loadProviders = useCallback(async () => {
        setLoadingProviders(true);
        try {
            // GET /api/messageprovider -> { success, data: [{id, name}], count }
            // Controller esta na 2talk Api → usar Api2Talk (igual omnichannel.js e phoneregistrationrequests.js).
            const res = await Api2Talk.get(`/messageprovider`);
            if (res?.data?.success) {
                const raw = res.data.data || [];
                const visible = raw
                    .map(normalizeProvider)
                    .filter((p) => p.id && PROVIDER_IDS_VISIBLE.includes(p.id))
                    // Whatsapp primeiro, Telegram depois (ordem do whitelist).
                    .sort((a, b) => PROVIDER_IDS_VISIBLE.indexOf(a.id) - PROVIDER_IDS_VISIBLE.indexOf(b.id));
                setProviders(visible);

                // Pre-seleciona Meta Whatsapp se o usuario ainda nao escolheu.
                if (!cfg.messageproviderid) {
                    const whats = visible.find((p) => p.id === PROVIDER_ID_META_WHATSAPP);
                    if (whats) update({ messageproviderid: whats.id });
                }
            }
        } catch (e) {
            Api2Talk.kickoff?.(e);
            setProviders([]);
        } finally {
            setLoadingProviders(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadNumbersByProvider = useCallback(async (providerId) => {
        if (!providerId) { setRegistrationNumbers([]); return; }
        setLoadingNumbers(true);
        try {
            // Endpoint dedicado: filtra ja no SQL pelo message_provider_id.
            // Controller esta na 2talk Api com rota base /api/registrationphonenumber
            // (NAO /registrationnumberrequest) — usar Api2Talk pra atingir o gateway certo.
            const res = await Api2Talk.get(`/registrationphonenumber/active-by-provider/${providerId}`);
            if (res?.data?.success) {
                const list = (res.data.data || []).map(normalizeNumber);
                setRegistrationNumbers(list);
            } else {
                setRegistrationNumbers([]);
            }
        } catch (e) {
            Api2Talk.kickoff?.(e);
            setRegistrationNumbers([]);
        } finally {
            setLoadingNumbers(false);
        }
    }, []);

    const loadTemplates = useCallback(async (registrationNumberRequestId) => {
        if (!registrationNumberRequestId) { setTemplates([]); return; }
        setLoadingTemplates(true);
        try {
            // Controller na 2talk Api — usar Api2Talk pra atingir o gateway certo.
            const res = await Api2Talk.get(`/templatemessage/approved/by-registration-number/${registrationNumberRequestId}`);
            if (res?.data?.success) {
                const list = (res.data.data || []).map(normalizeTemplate);
                setTemplates(list);
            } else {
                setTemplates([]);
            }
        } catch (e) {
            Api2Talk.kickoff?.(e);
            setTemplates([]);
        } finally {
            setLoadingTemplates(false);
        }
    }, []);

    const loadTemplateDetail = useCallback(async (templateId) => {
        if (!templateId) { setTemplateDetail(null); return; }
        setLoadingTemplateDetail(true);
        try {
            const res = await Api2Talk.get(`/templatemessage/with-includes/${templateId}`);
            if (res?.data?.success) setTemplateDetail(normalizeTemplate(res.data.data));
            else setTemplateDetail(null);
        } catch (e) {
            Api2Talk.kickoff?.(e);
            setTemplateDetail(null);
        } finally {
            setLoadingTemplateDetail(false);
        }
    }, []);

    // ============= EFFECTS =============

    useEffect(() => { loadProviders(); }, [loadProviders]);
    useEffect(() => { loadNumbersByProvider(cfg.messageproviderid); }, [cfg.messageproviderid, loadNumbersByProvider]);
    useEffect(() => { loadTemplates(cfg.registrationnumberrequestid); }, [cfg.registrationnumberrequestid, loadTemplates]);
    useEffect(() => { loadTemplateDetail(cfg.templatemessageid); }, [cfg.templatemessageid, loadTemplateDetail]);

    // Modo "envio direto" (ex.: tela de conversacao): provedor e numero sao FIXOS (props), nao
    // selecionaveis. Pre-seta ambos no value pra os effects acima carregarem os templates.
    useEffect(() => {
        if (!directSend) return;
        const patch = {};
        if (fixedProviderId && cfg.messageproviderid !== fixedProviderId) patch.messageproviderid = fixedProviderId;
        if (fixedRegistrationNumberRequestId && cfg.registrationnumberrequestid !== fixedRegistrationNumberRequestId) patch.registrationnumberrequestid = fixedRegistrationNumberRequestId;
        if (Object.keys(patch).length) update(patch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [directSend, fixedProviderId, fixedRegistrationNumberRequestId, cfg.messageproviderid, cfg.registrationnumberrequestid]);

    // ============= CHANGE HANDLERS =============

    const handleProviderChange = (provId) => {
        update({
            messageproviderid: provId || 0,
            registrationnumberrequestid: 0,
            templatemessageid: 0,
            parametersmappingjson: { header: {}, body: {}, button: {} },
        });
    };

    const handleNumberChange = (numId) => {
        update({
            registrationnumberrequestid: numId || 0,
            templatemessageid: 0,
            parametersmappingjson: { header: {}, body: {}, button: {} },
        });
    };

    const handleTemplateChange = (tplId) => {
        update({
            templatemessageid: tplId || 0,
            parametersmappingjson: { header: {}, body: {}, button: {} },
        });
    };

    const handleAddPhone = () => {
        if (!newPhone.trim()) return;
        if (!validateE164(newPhone)) {
            setNewPhoneError("Telefone inválido. Use formato E.164: +5511999999999");
            return;
        }
        const phones = [...(recipients.phones || []), newPhone.trim()];
        updateRecipients({ phones });
        setNewPhone("");
        setNewPhoneError("");
    };

    const handleRemovePhone = (idx) => {
        const phones = [...(recipients.phones || [])];
        phones.splice(idx, 1);
        updateRecipients({ phones });
    };

    const headerVars = extractVariables(templateDetail?.headerText);
    const bodyVars = extractVariables(templateDetail?.bodyText);

    // Tipos de placeholder relevantes na automacao de chamado (mesmo set do editor de email/webhook).
    const placeholderTypesForTicket = [
        PlaceType.Chamado,
        PlaceType.Solicitante,
        PlaceType.Empresa,
        PlaceType.Portal,
    ];

    const selectedProvider = providers.find((p) => p.id === cfg.messageproviderid);

    return (
        <Box sx={styles.container}>
            {!directSend && (
            <Box sx={{ display: "flex", alignItems: "center", marginBottom: "12px" }}>
                <WhatsAppIcon sx={{ color: "#25D366", marginRight: "8px" }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Disparo de mensagem ativa via provedor
                </Typography>
            </Box>
            )}

            {/* Modo direto: provedor/numero sao fixos (props). Mostra o numero de origem read-only. */}
            {directSend && (
                <Box sx={{ marginBottom: "12px" }}>
                    <Typography variant="caption" sx={{ color: "#888" }}>Número de origem (registrado)</Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                        {registrationNumbers.find((n) => n.id === cfg.registrationnumberrequestid)?.phoneNumber || (cfg.registrationnumberrequestid ? ("#" + cfg.registrationnumberrequestid) : "—")}
                    </Typography>
                </Box>
            )}

            <Grid container spacing={2}>
                {!directSend && (<>
                {/* ---- Provedor ---- */}
                <Grid item xs={12} md={4}>
                    <Autocomplete
                        size="small"
                        options={providers}
                        loading={loadingProviders}
                        getOptionLabel={(opt) => opt?.name || ""}
                        isOptionEqualToValue={(o, v) => o?.id === v?.id}
                        // Hoje so Whatsapp esta clicavel. Telegram aparece com chip "em breve".
                        getOptionDisabled={(opt) => !PROVIDER_IDS_CLICKABLE.includes(opt?.id)}
                        value={selectedProvider || null}
                        onChange={(_e, value) => handleProviderChange(value?.id)}
                        renderOption={(props, opt) => (
                            <li {...props}>
                                <span>{opt.name}</span>
                                {!PROVIDER_IDS_CLICKABLE.includes(opt.id) && (
                                    <Chip size="small" label="em breve" sx={{ ml: 1, fontSize: 10 }} />
                                )}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Provedor"
                                placeholder="Selecione..."
                                error={error && !cfg.messageproviderid}
                                InputLabelProps={{ shrink: true }}
                            />
                        )}
                    />
                </Grid>

                {/* ---- Numero ---- */}
                <Grid item xs={12} md={4}>
                    <Autocomplete
                        size="small"
                        options={registrationNumbers}
                        loading={loadingNumbers}
                        disabled={!cfg.messageproviderid || loadingNumbers}
                        getOptionLabel={(opt) => opt?.phoneNumber || ""}
                        isOptionEqualToValue={(o, v) => o?.id === v?.id}
                        value={registrationNumbers.find((n) => n.id === cfg.registrationnumberrequestid) || null}
                        onChange={(_e, value) => handleNumberChange(value?.id)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Número/canal"
                                placeholder="Selecione..."
                                error={error && !cfg.registrationnumberrequestid}
                                InputLabelProps={{ shrink: true }}
                            />
                        )}
                    />
                </Grid>
                </>)}

                {/* ---- Template ---- */}
                <Grid item xs={12} md={directSend ? 12 : 4}>
                    <Autocomplete
                        size="small"
                        options={templates}
                        loading={loadingTemplates}
                        disabled={!cfg.registrationnumberrequestid || loadingTemplates}
                        getOptionLabel={(opt) => opt?.name || ""}
                        isOptionEqualToValue={(o, v) => o?.id === v?.id}
                        value={templates.find((t) => t.id === cfg.templatemessageid) || null}
                        onChange={(_e, value) => handleTemplateChange(value?.id)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Template aprovado"
                                placeholder="Selecione..."
                                error={error && !cfg.templatemessageid}
                                InputLabelProps={{ shrink: true }}
                            />
                        )}
                    />
                </Grid>
            </Grid>

            <Divider sx={styles.sectionDivider} />

            {/* ---- Variaveis do template ---- */}
            <Typography sx={styles.sectionTitle}>Variáveis do template</Typography>
            {literalVariables && (
                <Alert severity="info" sx={{ marginBottom: "12px" }}>
                    Preencha o valor de cada variável do template. Os valores serão enviados exatamente como digitados.
                </Alert>
            )}
            {!literalVariables && (
            <Alert severity="info" sx={{ marginBottom: "12px" }}>
                Para cada variável do template, digite o valor diretamente, ou digite{" "}
                <code>{"{{"}</code> pra abrir a lista de placeholders Mexx, ou clique no botão{" "}
                <strong>+</strong> ao lado pra escolher do dicionário. Placeholders são substituídos
                com dados do chamado no momento do disparo.
            </Alert>
            )}

            {loadingTemplateDetail && (
                <Box sx={{ textAlign: "center", padding: "16px" }}>
                    <CircularProgress size={24} />
                </Box>
            )}

            {!loadingTemplateDetail && templateDetail && (
                <>
                    {/* Preview do template: ajuda o usuario a visualizar onde cada {{N}} aparece
                        no contexto e ver em tempo real o valor preenchido (chip verde = valor
                        definido, chip amarelo = sem valor ainda). */}
                    <TemplatePreview templateDetail={templateDetail} mapping={mapping} />

                    {headerVars.length > 0 && (
                        <Paper variant="outlined" sx={{ padding: "12px", marginBottom: "12px" }}>
                            <Typography sx={{ ...styles.sectionTitle, marginBottom: "12px" }}>
                                Cabeçalho (Header)
                            </Typography>
                            {headerVars.map((v) => (
                                <Box key={v} sx={styles.variableRow}>
                                    <VariableValueField
                                        label={v}
                                        value={(mapping.header || {})[v] || ""}
                                        onChange={(val) => updateMapping("header", v, val)}
                                        placeholderTypes={placeholderTypesForTicket}
                                        literalMode={literalVariables}
                                    />
                                </Box>
                            ))}
                        </Paper>
                    )}

                    {bodyVars.length > 0 && (
                        <Paper variant="outlined" sx={{ padding: "12px", marginBottom: "12px" }}>
                            <Typography sx={{ ...styles.sectionTitle, marginBottom: "12px" }}>
                                Corpo (Body)
                            </Typography>
                            {bodyVars.map((v) => (
                                <Box key={v} sx={styles.variableRow}>
                                    <VariableValueField
                                        label={v}
                                        value={(mapping.body || {})[v] || ""}
                                        onChange={(val) => updateMapping("body", v, val)}
                                        placeholderTypes={placeholderTypesForTicket}
                                        literalMode={literalVariables}
                                    />
                                </Box>
                            ))}
                        </Paper>
                    )}

                    {headerVars.length === 0 && bodyVars.length === 0 && (
                        <Alert severity="success" sx={{ marginBottom: "12px" }}>
                            Este template não possui variáveis. A mensagem será enviada exatamente como
                            aprovada.
                        </Alert>
                    )}
                </>
            )}

            {!directSend && (<>
            <Divider sx={styles.sectionDivider} />

            {/* ---- Destinatarios do ticket ---- */}
            <Typography sx={styles.sectionTitle}>Destinatários do chamado</Typography>
            <FormGroup row sx={{ marginBottom: "12px" }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={!!recipients.includeRequester}
                            onChange={(e) => updateRecipients({ includeRequester: e.target.checked })}
                        />
                    }
                    label="Solicitante"
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={!!recipients.includeAssigned}
                            onChange={(e) => updateRecipients({ includeAssigned: e.target.checked })}
                        />
                    }
                    label="Responsável"
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={!!recipients.includeObservant}
                            onChange={(e) => updateRecipients({ includeObservant: e.target.checked })}
                        />
                    }
                    label="Observador"
                />
            </FormGroup>

            <Divider sx={styles.sectionDivider} />

            {/* ---- Telefones avulsos ---- */}
            <Typography sx={styles.sectionTitle}>Telefones adicionais</Typography>
            <Alert severity="info" sx={{ marginBottom: "12px" }}>
                Aceita qualquer formato BR — <code>+5511999999999</code>, <code>5511999999999</code>,{" "}
                <code>(11) 99999-9999</code>, etc.
            </Alert>

            <Box sx={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="+5511999999999"
                    value={newPhone}
                    onChange={(e) => {
                        setNewPhone(e.target.value);
                        if (newPhoneError) setNewPhoneError("");
                    }}
                    onKeyDown={(e) => e.key === "Enter" && handleAddPhone()}
                    error={!!newPhoneError}
                    helperText={newPhoneError}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <PhoneIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddPhone}
                    sx={{ flexShrink: 0 }}
                >
                    Adicionar
                </Button>
            </Box>

            <Box>
                {(recipients.phones || []).length === 0 && (
                    <Typography variant="body2" sx={{ color: "#888", fontStyle: "italic" }}>
                        Nenhum telefone adicional cadastrado.
                    </Typography>
                )}
                {(recipients.phones || []).map((p, idx) => (
                    <Chip
                        key={`${p}-${idx}`}
                        label={p}
                        onDelete={() => handleRemovePhone(idx)}
                        deleteIcon={<DeleteIcon />}
                        sx={styles.phoneChip}
                        icon={<PhoneIcon />}
                    />
                ))}
            </Box>

            <Divider sx={styles.sectionDivider} />

            {/* ---- Resumo validacao ---- */}
            {(() => {
                const hasAnyTicketRecipient =
                    recipients.includeRequester || recipients.includeAssigned || recipients.includeObservant;
                const hasPhones = (recipients.phones || []).length > 0;
                if (!hasAnyTicketRecipient && !hasPhones) {
                    return (
                        <Alert severity="warning">
                            É necessário escolher pelo menos um destinatário (do chamado ou avulso).
                        </Alert>
                    );
                }
                return null;
            })()}
            </>)}
        </Box>
    );
}
